import { FC, useMemo, useState } from 'react';

import { useLazyQuery } from '@apollo/client';
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    Menu,
    MenuItem,
    Typography,
    useTheme,
} from '@mui/material';
import { convertsCentsToCurrency, convertsCurrencyToCents } from '@npi/buddy-utils';
import { InvoiceStatus } from '@prisma/client';
import dayjs from 'dayjs';
import { Formik } from 'formik';
import { BsX } from 'react-icons/bs';

import { useParams } from '@redwoodjs/router';
import { useMutation } from '@redwoodjs/web';

import { DisplayCurrency, InfoBanner, TotalCard } from 'src/components';
import { Flex } from 'src/components/Flex';
import Loader from 'src/components/Loader/Loader';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import ViewPDFModal from 'src/components/ViewPDFModal';
import useSelfBillingInvoice from 'src/features/finance/selfBillingSalesInvoice/hooks/useSelfBillingInvoice';
import selfBillingInvoiceBannerConfigs from 'src/features/finance/selfBillingSalesInvoice/view/components/selfBillingInvoiceBannerConfigs';
import ViewSelfBillingInvoiceCallToActions from 'src/features/finance/selfBillingSalesInvoice/view/components/ViewSelfBillingInvoiceCallToActions/ViewSelfBillingInvoiceCallToActions';
import {
    RESUBMIT_TO_SELF_BILLING_INVOICE_MUTATION,
    UPDATE_SELF_BILLING_INVOICE_STATUS,
} from 'src/graphql/mutations/selfBillingInvoice.mutation';
import { GET_SELF_BILLING_INVOICE_PDF } from 'src/graphql/queries/getSelfBillingInvoices.query';
import { useAllocateInvoicePayment } from 'src/hooks';
import { useCurrency } from 'src/hooks/useCurrency';
import { PageHeader } from 'src/layouts/MainLayout/PageHeader';
import { EnumToArray, FormatBillingDetails, enumToCamelCase } from 'src/lib/formatters';
import { onError } from 'src/utils/utils';
import { AllocatePaymentValidation } from 'src/validation/SalesInvoice';
import { ResubmitToSageSelfBillingInvoice } from 'types/graphql';

import {
    AddressDetails,
    PartnerBillingDetails,
    SelfBillingInvoiceDetails,
} from '../ViewSelfBillingInvoicePage/components';

import {
    CommentsSection,
    InvoiceHistoryTable,
    InvoiceItems,
    InvoicePaymentHistoryTable,
} from './components';
import SendSelfBillingInvoiceModal from './components/SendSelfBillingInvoiceModal';
const ViewSelfBillingInvoicePage: FC = () => {
    const { id } = useParams();
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [isPDFModalOpen, setIsPDFModalOpen] = useState(false);
    const [isSendModalOpen, setIsSendModalOpen] = useState(false);
    const [statusMenuOpen, setStatusMenuOpen] = useState(false);
    const [showPaymentAllocationModal, setShowPaymentAllocationModel] = useState(false);
    const today = dayjs();

    const {
        selfBillingInvoice,
        refetchSelfBillingInvoice,
        isLoadingSelfBillingInvoice,
        isAllowedToAllocatePaymentToSelfBillingInvoice,
        isSelfBillingInvoiceCreateFailed,
        isSelfBillingInvoiceProcessing,
        isSelfBillingInvoiceReleaseFailed,
    } = useSelfBillingInvoice({ id });

    const { allocateInvoicePayment, isAllocatingPayment } = useAllocateInvoicePayment(id, 'SELF_BILLING');

    const { currencySymbol } = useCurrency(selfBillingInvoice?.partner?.countryDetails);

    const bannerConfigs = selfBillingInvoiceBannerConfigs(selfBillingInvoice?.status, theme);


    const billingContactName = `${
        selfBillingInvoice?.partner?.billingContactInformation?.bci_firstName ?? ''
    } ${selfBillingInvoice?.partner?.billingContactInformation?.bci_lastName ?? ''}`;

    const billingDetails = useMemo(() => {
        const billingContactInformation = selfBillingInvoice?.partner?.billingContactInformation;
        return FormatBillingDetails(billingContactInformation);
    }, [selfBillingInvoice]);

    const [getSelfBillingInvoicePDF, { data: selfBillingInvoicePDF, loading: isLoadingPDF }] =
        useLazyQuery(GET_SELF_BILLING_INVOICE_PDF, {
            fetchPolicy: 'cache-and-network',
            onError,
        });

    const handleOpenPDFModal = async () => {
        setIsPDFModalOpen(true);
        await getSelfBillingInvoicePDF({
            variables: {
                id: Number.parseInt(id, 10),
            },
        });
    };

    const [updateSelfBillingInvoiceStatus] = useMutation(UPDATE_SELF_BILLING_INVOICE_STATUS, {
        variables: { id: Number.parseInt(id, 10) },
        onError: (error) => {
            return showErrorToast(error.message);
        },
        onCompleted: () => {
            showSuccessToast('Self-Billing Invoice Status Updated!');
            setStatusMenuOpen(false);
            onRefreshAll();
            setAnchorEl(null);
        },
    });

    const onRefreshAll = () => {
        refetchSelfBillingInvoice();
    };

    const onStatusSelect = async (status: InvoiceStatus) => {
        await updateSelfBillingInvoiceStatus({
            variables: { status, invoiceId: id },
        });
    };

    const emptyPdfBlob = new Blob([], { type: 'application/pdf' });
    const file = new File([emptyPdfBlob], `self-billing-invoice-${id}.pdf`, {
        type: 'application/pdf',
    });

    const handleOpenSendModal = () => {
        setIsSendModalOpen(true);
    };

    const handleCloseSendModal = () => {
        setIsSendModalOpen(false);
    };

    const [resubmitToSageSelfBillingInvoice] = useMutation<ResubmitToSageSelfBillingInvoice>(
        RESUBMIT_TO_SELF_BILLING_INVOICE_MUTATION,
        {
            onCompleted: () => {
                showSuccessToast('Invoice resubmitted to Sage successfully');
                refetchSelfBillingInvoice();
            },
            onError: () => {
                showErrorToast(`Invoice unsuccessfully reprocessed`);
            },
        }
    );

    const handleResubmitToSage = () => {
        resubmitToSageSelfBillingInvoice({
            variables: {
                id: Number.parseInt(id, 10),
            },
        });
    };

    return (
        <Formik
            onSubmit={async (values, actions) => {
                const { paymentAmount, paymentMethod, balance, date } = values;
                const paymentAmountInCents = convertsCurrencyToCents(Number.parseFloat(paymentAmount));
                const balanceInCents = convertsCurrencyToCents(balance);
                const totalInvoiceAmount = selfBillingInvoice?.totals?.invoiceAmount ?? 0;

                await allocateInvoicePayment(
                    paymentAmountInCents,
                    paymentMethod,
                    balanceInCents,
                    date.toDate(),
                    totalInvoiceAmount
                );
                refetchSelfBillingInvoice({ id: Number.parseInt(id, 10) });
                setShowPaymentAllocationModel(false);
                actions.resetForm();
            }}
            initialValues={{
                status: selfBillingInvoice?.status,
                balance: convertsCentsToCurrency(selfBillingInvoice?.totals?.outstandingAmount),
                totalInvoiceAmount: convertsCentsToCurrency(
                    selfBillingInvoice?.totals?.invoiceAmount ?? 0
                ),
                paymentAmount: convertsCentsToCurrency(selfBillingInvoice?.totals?.outstandingAmount).toFixed(
                    2
                ),
                paymentMethod: null,
                date: today,
            }}
            enableReinitialize={!isAllocatingPayment}
            validationSchema={AllocatePaymentValidation}
        >
            {({ handleSubmit }) => {
                return (
                    <Flex flexDirection="column" gap="24px">
                        <PageHeader
                            heading="View Self-Billing Invoice"
                            subHeading={selfBillingInvoice?.partner?.business?.legalName ?? ''}
                            callToActions={
                                <ViewSelfBillingInvoiceCallToActions
                                    isSelfBillingInvoiceCreateFailed={isSelfBillingInvoiceCreateFailed}
                                    isSelfBillingInvoiceProcessing={isSelfBillingInvoiceProcessing}
                                    isSelfBillingInvoiceReleaseFailed={isSelfBillingInvoiceReleaseFailed}
                                    isAllowedToAllocatePaymentToSelfBillingInvoice={
                                        isAllowedToAllocatePaymentToSelfBillingInvoice
                                    }
                                    handleReleaseSelfBillingInvoice={function (): void {
                                        throw new Error('Function not implemented.');
                                    }}
                                    handleResubmitToSage={function (): void {
                                        handleResubmitToSage();
                                    }}
                                    handleViewPDFModal={handleOpenPDFModal}
                                    handleOpenSendModal={handleOpenSendModal}
                                    allocating={isAllocatingPayment}
                                    setShowPaymentAllocationModel={setShowPaymentAllocationModel}
                                    showPaymentAllocationModal={showPaymentAllocationModal}
                                    totalOutstanding={selfBillingInvoice?.totals?.outstandingAmount}
                                    onAllocatePayment={handleSubmit}
                                />
                            }
                        />

                        {bannerConfigs ? (
                            <InfoBanner
                                title={bannerConfigs.title}
                                subText={bannerConfigs.subText}
                                styles={bannerConfigs.styles}
                            />
                        ) : null}

                        <Flex flexDirection="column" gap="24px">
                            <PartnerBillingDetails
                                partnerName={billingContactName ?? ''}
                                partnerTelephone={
                                    billingDetails.contactNumber ?? selfBillingInvoice?.partner?.telephone ?? ''
                                }
                                partnerEmailAddress={billingDetails?.email}
                            />
                        </Flex>
                        <Menu
                            id="basic-menu"
                            anchorEl={anchorEl}
                            open={statusMenuOpen}
                            onClose={() => {
                                return setStatusMenuOpen(false);
                            }}
                            MenuListProps={{
                                'aria-labelledby': 'basic-button',
                            }}
                        >
                            {EnumToArray(InvoiceStatus)?.map((item) => {
                                return (
                                    <MenuItem
                                        key={item}
                                        onClick={() => {
                                            return onStatusSelect(item);
                                        }}
                                    >
                                        {enumToCamelCase(item)}
                                    </MenuItem>
                                );
                            })}
                        </Menu>
                        {isLoadingSelfBillingInvoice ? (
                            <Loader />
                        ) : (
                            <>
                                <Flex gap="inherit">
                                    <Flex flexDirection="column" flex={4}>
                                        <SelfBillingInvoiceDetails
                                            invoiceStatus={selfBillingInvoice?.status ?? ''}
                                            invoiceDate={selfBillingInvoice?.invoiceDate ?? ''}
                                            invoiceNumber={selfBillingInvoice?.sageInvoiceNumber ?? ''}
                                            partnerReference={
                                                selfBillingInvoice?.partner?.partnerAccountNumber ?? ''
                                            }
                                        />
                                    </Flex>
                                </Flex>

                                <Flex gap="inherit">
                                    <Flex flexDirection="column" flex={4}>
                                        <AddressDetails />
                                    </Flex>
                                </Flex>
                                <Box display={'flex'} gap="19px" justifyContent={'space-between'}>
                                    <TotalCard
                                        title="Total Net Amount"
                                        value={
                                            <DisplayCurrency
                                                disableStyling
                                                usersCurrency={currencySymbol}
                                                centsAmount={selfBillingInvoice?.totals?.netAmount}
                                            />
                                        }
                                        labelTypographyStyles={{
                                            fontSize: '18px',
                                            fontWeight: '600',
                                        }}
                                        valueTypographyStyles={{
                                            fontSize: '24px',
                                        }}
                                        containerStyles={{
                                            borderRadius: '16px',
                                            width: '100%',
                                        }}
                                        titleContainerStyles={{
                                            borderTopLeftRadius: '15px',
                                            borderTopRightRadius: '15px',
                                            height: '100%',
                                        }}
                                    />
                                    <TotalCard
                                        title={`Total Vat Amount`}
                                        value={
                                            <DisplayCurrency
                                                disableStyling
                                                usersCurrency={currencySymbol}
                                                centsAmount={selfBillingInvoice?.totals?.vatAmount}
                                            />
                                        }
                                        labelTypographyStyles={{
                                            fontSize: '18px',
                                            fontWeight: '600',
                                        }}
                                        valueTypographyStyles={{
                                            fontSize: '24px',
                                        }}
                                        containerStyles={{
                                            borderRadius: '16px',
                                            width: '100%',
                                        }}
                                        titleContainerStyles={{
                                            borderTopLeftRadius: '15px',
                                            borderTopRightRadius: '15px',
                                            height: '100%',
                                        }}
                                    />
                                    <TotalCard
                                        title="Total Invoice Amount"
                                        value={
                                            <DisplayCurrency
                                                disableStyling
                                                usersCurrency={currencySymbol}
                                                centsAmount={selfBillingInvoice?.totals?.invoiceAmount}
                                            />
                                        }
                                        labelTypographyStyles={{
                                            fontSize: '18px',
                                            fontWeight: '600',
                                        }}
                                        valueTypographyStyles={{
                                            fontSize: '24px',
                                        }}
                                        containerStyles={{
                                            borderRadius: '16px',
                                            width: '100%',
                                        }}
                                        titleContainerStyles={{
                                            borderTopLeftRadius: '15px',
                                            borderTopRightRadius: '15px',
                                            height: '100%',
                                        }}
                                    />
                                    <TotalCard
                                        title="Invoice Amount Outstanding"
                                        value={
                                            <DisplayCurrency
                                                disableStyling
                                                usersCurrency={currencySymbol}
                                                centsAmount={selfBillingInvoice?.totals?.outstandingAmount}
                                            />
                                        }
                                        labelTypographyStyles={{
                                            fontSize: '18px',
                                            fontWeight: '600',
                                        }}
                                        valueTypographyStyles={{
                                            fontSize: '24px',
                                        }}
                                        containerStyles={{
                                            borderRadius: '16px',
                                            width: '100%',
                                        }}
                                        titleContainerStyles={{
                                            borderTopLeftRadius: '15px',
                                            borderTopRightRadius: '15px',
                                            height: '100%',
                                        }}
                                    />
                                </Box>
                                <InvoiceItems
                                    lineItems={selfBillingInvoice?.invoiceItems}
                                    usersCurrency={currencySymbol}
                                />

                                <InvoiceHistoryTable
                                    invoice={selfBillingInvoice}
                                    currencySymbol={currencySymbol}
                                />
                                <CommentsSection comments={selfBillingInvoice?.comments} />

                                <InvoicePaymentHistoryTable
                                    invoice={selfBillingInvoice}
                                    currencySymbol={currencySymbol}
                                />
                                {isLoadingPDF ? (
                                    <Loader />
                                ) : (
                                    <ViewPDFModal
                                        title="View Self-Billing Invoice PDF"
                                        file={file}
                                        fileURL={selfBillingInvoicePDF?.getSelfBillingInvoicePDF?.downloadLink}
                                        isModalOpen={isPDFModalOpen}
                                        maxWidth="lg"
                                        setIsModalOpen={setIsPDFModalOpen}
                                    />
                                )}
                                <Dialog
                                    open={isSendModalOpen}
                                    onClose={handleCloseSendModal}
                                    aria-labelledby="send-pdf-modal"
                                    sx={{
                                        '& .MuiPaper-root': {
                                            margin: '0px',
                                            padding: '0px',
                                            borderRadius: '16px',
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            background: 'linear-gradient(90deg, #FF8008, #FFC837)',
                                            height: '82px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            padding: '24px',
                                        }}
                                    >
                                        <Typography color="white" variant="h2">
                                            Resend Self-Billing Invoice
                                        </Typography>
                                        <Button
                                            variant="outlined"
                                            startIcon={<BsX width="24px" height="24px" />}
                                            onClick={handleCloseSendModal}
                                            sx={{
                                                color: '#F39E4E',
                                                minWidth: '32px !important',
                                                width: '32px',
                                                height: '32px',
                                                padding: '0px',
                                                borderRadius: '50%',
                                                backgroundColor: 'white',
                                                border: '1px solid #F39E4E',
                                                '& span': {
                                                    marginRight: '0px',
                                                    marginLeft: '0px',
                                                },
                                                '&:hover': {
                                                    backgroundColor: '#eee !important',
                                                },
                                                boxShadow: 'none',
                                            }}
                                        />
                                    </Box>
                                    <DialogContent>
                                        <SendSelfBillingInvoiceModal
                                            handleCloseSendModal={handleCloseSendModal}
                                            invoiceId={Number.parseInt(id, 10)}
                                            partnerBillingContactEmail={billingDetails.email}
                                        />
                                    </DialogContent>
                                </Dialog>
                            </>
                        )}
                    </Flex>
                );
            }}
        </Formik>
    );
};

export default ViewSelfBillingInvoicePage;
